import React, { useState, useEffect } from "react";
import {
  Box, Grid, Table, TableHead, TableBody, TableRow, TableCell, Button, TextField,
  Dialog, DialogActions, DialogContent, Typography, Select, MenuItem, IconButton, Tooltip,
  useMediaQuery, useTheme, TableContainer, Paper, DialogTitle, DialogContentText,
  FormControl, InputLabel
} from "@mui/material";
import { putRawMaterialMachineMappingAction, getMachineByIdAction, rawMaterialMachineMappingAction, getRawMaterialsAction, deleteRawMaterialMachineMappingAction, clearPutRawMaterialMachineMappingAction, clearRawMaterialMachineMappingAction } from '../../store/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';

const MachineRawMaterialMapping = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const machineId = location.state?.machineId;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [rawMaterials, setRawMaterials] = useState([]);
  const [productModel, setProductModel] = useState('');
  const [estimateDay, setEstimateDay] = useState('');
  const [open, setOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [editFormData, setEditFormData] = useState({ rawMaterial: '', quantity: '' });
  // Add state for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [tableSearchText, setTableSearchText] = useState('');

  const { successData: getMachineById } = useSelector((state) => state.getMachineByIdReducer);
  const { successData: rawMaterialsData } = useSelector((state) => state.getRawMaterialsReducer);
  const { successData: rawMaterialMachineMapping } = useSelector((state) => state.rawMaterialMachineMappingReducer);
  const { successData: updatedRawMaterialData } = useSelector((state) => state.putRawMaterialMachineMappingReducer);
  const { successData: deleteRawMaterialMapping } = useSelector((state) => state.deleteRawMaterialMachineMappingReducer);

  useEffect(() => {
    if (machineId) {
      dispatch(getMachineByIdAction({
        endPoint: `admin/machine/${machineId}`
      }));
      dispatch(getRawMaterialsAction());
      dispatch(clearPutRawMaterialMachineMappingAction())
      dispatch(clearRawMaterialMachineMappingAction())
    }
  }, [dispatch, machineId]);

  useEffect(() => {
    if (getMachineById) {
      setProductModel(getMachineById?.data.name);
      setEstimateDay(getMachineById?.data.edp);
      if (getMachineById?.data.MachineRawMaterialMappings?.length > 0) {
        const mappedRawMaterials = getMachineById.data.MachineRawMaterialMappings.map(mapping => ({
          id: mapping.id,
          rawMaterial: mapping.raw_material_id,
          quantity: mapping.quantity,
          name: getRawMaterialName(mapping.raw_material_id)
        }));
        setRawMaterials(mappedRawMaterials);
      } else {
        setRawMaterials([]);
      }
    }
  }, [getMachineById, rawMaterialsData]);

  useEffect(() => {
    if (rawMaterialMachineMapping) {
      toast.success('Raw materials updated successfully');
      dispatch(getMachineByIdAction({
        endPoint: `admin/machine/${machineId}`
      }));
      handleClose();
    }
  }, [rawMaterialMachineMapping, dispatch, machineId]);

  useEffect(() => {
    if (updatedRawMaterialData) {
      toast.success('Raw materials updated successfully');
      handleClose();
      dispatch(getMachineByIdAction({
        endPoint: `admin/machine/${machineId}`
      }));
    }
  }, [updatedRawMaterialData, dispatch, machineId]);

  // Add effect for delete success
  useEffect(() => {
    if (deleteRawMaterialMapping) {
      toast.success('Raw material mapping deleted successfully');
      // Refresh the data
      dispatch(getMachineByIdAction({
        endPoint: `admin/machine/${machineId}`
      }));
    }
  }, [deleteRawMaterialMapping, dispatch, machineId]);

  const getRawMaterialName = (id) => {
    if (!rawMaterialsData?.data) return "Loading...";
    const material = rawMaterialsData.data.find(item => item.id === id);
    return material ? material.name : "Unknown";
  };

  const handleEditClick = (material) => {
    setEditingMaterial(material);
    setEditFormData({
      rawMaterial: material.rawMaterial,
      quantity: material.quantity
    });
    setOpen(true);
  };

  const handleAddNewClick = () => {
    setEditingMaterial(null);
    setEditFormData({ rawMaterial: '', quantity: '' });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingMaterial(null);
    setEditFormData({ rawMaterial: '', quantity: '' });
    setSearchText('');
  };

  const handleInputChange = (field, value) => {
    setEditFormData({
      ...editFormData,
      [field]: value
    });
  };

  const handleSave = () => {
    if (editingMaterial) {
      // Update existing mapping
      dispatch(putRawMaterialMachineMappingAction({
        endPoint: `admin/rawmaterialmapping/edit/${editingMaterial.id}`,
        machine_id: machineId,
        raw_materials: [{
          raw_material_id: editFormData.rawMaterial,
          quantity: editFormData.quantity
        }]
      }));
    } else {
      // Add new mapping
      dispatch(rawMaterialMachineMappingAction({
        machine_id: machineId,
        raw_materials: [{
          raw_material_id: editFormData.rawMaterial,
          quantity: editFormData.quantity
        }]
      }));
    }
  };

  // Modified to open confirmation dialog
  const handleDeleteClick = (material) => {
    setMaterialToDelete(material);
    setDeleteDialogOpen(true);
  };

  // New function to handle actual deletion
  const confirmDelete = () => {
    if (materialToDelete) {
      dispatch(deleteRawMaterialMachineMappingAction({
        endPoint: `admin/rawmaterialmapping/delete/${materialToDelete.id}`,
        machine_id: machineId,
      }));
      setDeleteDialogOpen(false);
      setMaterialToDelete(null);
    }
  };

  // New function to close delete dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setMaterialToDelete(null);
  };

  // Filtered materials for the table
  const filteredMaterials = rawMaterials.filter(material =>
    material.name.toLowerCase().includes(tableSearchText.toLowerCase())
  );

  return (
    <Box sx={{
      p: { xs: 1, sm: 3 },
      width: { xs: '95%', sm: '90%', md: '75%' },
      ml: { xs: '2.5%', sm: '5%', md: '20%' },
      mt: { xs: '20%', sm: '15%', md: '7%' }
    }}>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <h2 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>Product And Raw Material Mapping</h2>
      </Box>

      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Product And Model"
            variant="outlined"
            value={productModel}
            disabled
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Estimate day"
            variant="outlined"
            value={estimateDay}
            disabled
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddNewClick}
          size={isMobile ? "small" : "medium"}
        >
          Add Raw Material
        </Button>
      </Box>

      {/* Search field above table */}
      <Box sx={{ mb: 2 }}>
        <TextField
          width = "50%"
          variant="outlined"
          label="Search Raw Materials"
          placeholder="Search by raw material"
          value={tableSearchText}
          onChange={(e) => setTableSearchText(e.target.value)}
          size={isMobile ? "small" : "medium"}
          InputProps={{
            startAdornment: (
              <Box component="span" sx={{ color: 'action.active', mr: 1 }}>
                🔍
              </Box>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell>Raw Material</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMaterials.length > 0 ? (
              filteredMaterials.map((material, index) => (
                <TableRow key={index}>
                  <TableCell>{material.name}</TableCell>
                  <TableCell>{material.quantity}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ mr: isMobile ? 0 : 1, mb: isMobile ? 1 : 0 }}
                        onClick={() => handleEditClick(material)}
                        size={isMobile ? "small" : "medium"}
                        fullWidth={isMobile}
                      >
                        Update
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDeleteClick(material)}
                        size={isMobile ? "small" : "medium"}
                        fullWidth={isMobile}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  {rawMaterials.length > 0
                    ? 'No matching raw materials found'
                    : 'No raw materials mapped to this product'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Add Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            width: isMobile ? '90%' : '500px',
            margin: isMobile ? '10px' : 'auto',
            borderRadius: '8px'
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: isMobile ? 1.5 : 2,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant={isMobile ? "h6" : "h5"} component="h2">
            {editingMaterial ? 'Edit Raw Material' : 'Add Raw Material'}
          </Typography>
          <IconButton onClick={handleClose} size={isMobile ? "small" : "medium"}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 1
          }}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="raw-material-label">Raw Material</InputLabel>
              <Select
                labelId="raw-material-label"
                value={editFormData.rawMaterial}
                onChange={(e) => handleInputChange('rawMaterial', e.target.value)}
                label="Raw Material"
                disabled={editingMaterial !== null}
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiSelect-select': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300
                    }
                  }
                }}
              >
                <MenuItem value="" disabled>Select Raw Material</MenuItem>
                {/* Search input at the top of dropdown */}
                <MenuItem
                  value=""
                  sx={{
                    p: 0,
                    position: 'sticky',
                    top: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1,
                    borderBottom: '1px solid #e0e0e0'
                  }}
                  disableRipple
                  disableTouchRipple
                >
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="Search raw materials..."
                    fullWidth
                    variant="outlined"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      // Store search text in component state
                      const searchText = e.target.value.toLowerCase();
                      setSearchText(searchText);
                    }}
                    onKeyDown={(e) => {
                      // Prevent closing dropdown when typing
                      if (e.key !== 'Escape' && e.key !== 'Tab') {
                        e.stopPropagation();
                      }
                    }}
                    sx={{ m: 1 }}
                  />
                </MenuItem>
                {rawMaterialsData?.data
                  ?.slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .filter(material =>
                    !searchText || material.name.toLowerCase().includes(searchText.toLowerCase())
                  )
                  .map((rawMaterial) => (
                    <MenuItem
                      key={rawMaterial.id}
                      value={rawMaterial.id}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {rawMaterial.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <TextField
              label="Quantity"
              type="number"
              value={editFormData.quantity}
              onChange={(e) => handleInputChange('quantity', e.target.value)}
              fullWidth
              variant="outlined"
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{
          p: isMobile ? 1.5 : 2,
          justifyContent: 'space-between',
          borderTop: '1px solid #e0e0e0'
        }}>
          <Button
            onClick={handleClose}
            color="secondary"
            size={isMobile ? "small" : "medium"}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={!editFormData.rawMaterial || !editFormData.quantity}
            size={isMobile ? "small" : "medium"}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Deletion"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this raw material mapping?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            No
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MachineRawMaterialMapping;

